// Pagina de Bairros — orquestracao.
// CRUD de bairros + valor de entrega + CEP unico + pedido minimo.

import { useCallback, useMemo, useState } from "react";
import { DollarSign, MapPin, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Skeleton } from "../../components/ui/skeleton";
import { CarregandoTabela } from "../../components/carregando";

import { bairrosApi } from "./api/bairrosApi";
import { useBairrosData } from "./hooks/useBairrosData";
import { useBairroForm } from "./hooks/useBairroForm";
import { useCepUnico } from "./hooks/useCepUnico";
import { useValorMassa } from "./hooks/useValorMassa";
import { usePedidoMinimo } from "./hooks/usePedidoMinimo";
import { normalizarNome, useBairrosUsage } from "./hooks/useBairrosUsage";

import ConfigBar from "./components/ConfigBar";
import BairrosTable from "./components/BairrosTable";
import BairroFormDialog from "./components/BairroFormDialog";
import CepUnicoDialog from "./components/CepUnicoDialog";
import ValorMassaDialog from "./components/ValorMassaDialog";

// Esqueleto dos dois cartões de configuração (CEP Único e Pedido Mínimo) na
// primeira abertura — mesma altura dos cartões de verdade.
const ConfigBarCarregando = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" aria-busy="true" aria-label="Carregando">
    {[0, 1].map((i) => (
      <div key={i} className="rounded-xl border bg-card p-3 flex items-center gap-3">
        <Skeleton className="w-9 h-9 rounded-lg shrink-0" />
        <div className="flex flex-col gap-1.5 flex-1 min-w-0">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-3 w-48 max-w-full" />
        </div>
        <Skeleton className="h-9 w-24 rounded-lg shrink-0" />
      </div>
    ))}
  </div>
);

export default function Bairros() {
  const { bairros, loading, refresh, cepUnico, cepUnicoValue, setCepUnico, setCepUnicoValue } =
    useBairrosData();

  // Modais e formularios isolados por hook
  const form = useBairroForm({ onSucesso: refresh, cepUnico, cepUnicoValue });
  const cep = useCepUnico({
    bairros,
    cepUnico,
    cepUnicoValue,
    setCepUnico,
    setCepUnicoValue,
    onSucesso: refresh,
  });
  const valorMassa = useValorMassa({ onSucesso: refresh });
  const pedidoMinimo = usePedidoMinimo();
  const usage = useBairrosUsage();

  const [searchTerm, setSearchTerm] = useState("");
  // Ordenação pelos cabeçalhos da tabela (clique alterna asc/desc)
  const [sort, setSort] = useState({ campo: "nome", dir: "asc" });

  const alternarSort = useCallback((campo) => {
    setSort((atual) => {
      if (atual.campo === campo) {
        return { campo, dir: atual.dir === "asc" ? "desc" : "asc" };
      }
      // Direção inicial que faz sentido por coluna: nome A→Z; uso/valor maior primeiro
      return { campo, dir: campo === "nome" ? "asc" : "desc" };
    });
  }, []);

  // Enriquece cada bairro com contagem e % de utilização. Match primário por
  // bairro_id (FK gravada na criação, imune a rename); fallback por nome
  // normalizado pra pedidos sem vínculo (iFood texto-livre, legado).
  const bairrosComUso = useMemo(
    () =>
      bairros.map((b) => {
        const usoCount =
          (usage.porBairroId[b.id] || 0) + (usage.porBairro[normalizarNome(b.nome)] || 0);
        const usoPct = usage.total > 0 ? (usoCount / usage.total) * 100 : null;
        return { ...b, usoCount, usoPct };
      }),
    [bairros, usage],
  );

  const bairrosFiltrados = useMemo(() => {
    let lista = bairrosComUso;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      lista = lista.filter((b) => b.nome.toLowerCase().includes(q));
    }
    const porNome = (a, b) => a.nome.localeCompare(b.nome, "pt-BR");
    const mult = sort.dir === "asc" ? 1 : -1;
    const comparadores = {
      nome: (a, b) => mult * porNome(a, b),
      uso: (a, b) => mult * (a.usoCount - b.usoCount) || porNome(a, b),
      valor: (a, b) =>
        mult * ((Number(a.valor_entrega) || 0) - (Number(b.valor_entrega) || 0)) ||
        porNome(a, b),
    };
    return [...lista].sort(comparadores[sort.campo] || comparadores.nome);
  }, [bairrosComUso, searchTerm, sort]);

  // Edição inline do valor de entrega (célula da tabela)
  const salvarValorInline = useCallback(
    async (bairro, novoValor) => {
      try {
        await bairrosApi.atualizar(bairro.id, { valor_entrega: novoValor });
        toast.success(
          `${bairro.nome}: entrega atualizada para R$ ${novoValor.toFixed(2).replace(".", ",")}`,
        );
        refresh();
      } catch (e) {
        toast.error("Erro ao atualizar o valor de entrega");
      }
    },
    [refresh],
  );

  // Resumo pro subtítulo: "12 bairros · entrega média R$ 7,50"
  const resumo = useMemo(() => {
    if (!bairros.length) return "Gerencie os bairros e valores de entrega";
    const media =
      bairros.reduce((s, b) => s + (Number(b.valor_entrega) || 0), 0) / bairros.length;
    const plural = bairros.length === 1 ? "bairro" : "bairros";
    return `${bairros.length} ${plural} · entrega média R$ ${media.toFixed(2).replace(".", ",")}`;
  }, [bairros]);

  // Carregamento com cards (25/10/2026): título e botões aparecem na hora; os
  // cartões de configuração e a tabela piscam até bairros, pedido mínimo e uso
  // chegarem. `loading` (e os "carregado") só mudam na 1ª leitura — salvar não pisca.
  const carregandoConfig = loading || !pedidoMinimo.carregado;
  const carregandoTabela = loading || !usage.carregado;

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MapPin className="w-7 h-7 text-primary" />
            Bairros
          </h1>
          <p className="text-muted-foreground text-sm mt-1">{resumo}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={valorMassa.abrir}>
            <DollarSign className="w-4 h-4 mr-1" />
            Alterar Todos
          </Button>
          <Button onClick={() => form.abrir()}>
            <Plus className="w-4 h-4 mr-1" />
            Novo Bairro
          </Button>
        </div>
      </div>

      {carregandoConfig ? <ConfigBarCarregando /> : (
      <ConfigBar
        cepUnico={cepUnico}
        cepUnicoValue={cepUnicoValue}
        onCepSwitchChange={cep.onSwitchChange}
        pedidoMinimoValor={pedidoMinimo.valor}
        pedidoMinimoSaving={pedidoMinimo.saving}
        pedidoMinimoTouched={pedidoMinimo.touched}
        onPedidoMinimoChange={pedidoMinimo.handleChange}
        onPedidoMinimoSalvar={pedidoMinimo.salvar}
      />
      )}

      {/* Busca (centralizada) + tabela com ordenação nos cabeçalhos */}
      <div className="space-y-3">
        {!carregandoTabela && bairros.length > 0 && (
          <div className="relative w-full max-w-2xl mx-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Buscar bairro..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        )}

        {carregandoTabela ? (
          <CarregandoTabela linhas={8} colunas={5} className="rounded-xl border bg-card overflow-hidden" />
        ) : bairros.length > 0 && bairrosFiltrados.length === 0 ? (
          // Busca sem resultado: antes caía em "Nenhum bairro cadastrado / Cadastrar
          // primeiro bairro", como se a loja não tivesse bairro nenhum.
          <div className="text-center py-12 rounded-xl border bg-card text-muted-foreground">
            Nenhum bairro encontrado para "{searchTerm}"
          </div>
        ) : (
        <BairrosTable
          bairros={bairrosFiltrados}
          windowDays={usage.windowDays}
          sort={sort}
          onSort={alternarSort}
          onNovo={() => form.abrir()}
          onEditar={form.abrir}
          onDeletar={form.deletar}
          onSalvarValor={salvarValorInline}
        />
        )}
      </div>

      <BairroFormDialog
        open={form.open}
        onOpenChange={form.setOpen}
        editando={form.editando}
        formData={form.formData}
        setFormData={form.setFormData}
        saving={form.saving}
        onSalvar={form.salvar}
        cepUnico={cepUnico}
        cepUnicoValue={cepUnicoValue}
      />

      <CepUnicoDialog
        open={cep.modalOpen}
        onOpenChange={cep.setModalOpen}
        novoCep={cep.novoCep}
        setNovoCep={cep.setNovoCep}
        saving={cep.saving}
        onConfirmar={cep.confirmar}
        totalBairros={bairros.length}
      />

      <ValorMassaDialog
        open={valorMassa.modalOpen}
        onOpenChange={valorMassa.setModalOpen}
        valor={valorMassa.valor}
        setValor={valorMassa.setValor}
        modo={valorMassa.modo}
        setModo={valorMassa.setModo}
        operacao={valorMassa.operacao}
        setOperacao={valorMassa.setOperacao}
        saving={valorMassa.saving}
        onConfirmar={valorMassa.confirmar}
        totalBairros={bairros.length}
        cepUnico={cepUnico}
      />
    </div>
  );
}
